import React from 'react';

import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';

import type {
  HtmlDocumentTemplateFieldsFragment,
  HtmlDocumentTemplateInput,
  HtmlDocumentTemplateStyleInput
} from '~/api/graphql';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import {
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import {
  buildHeaderFooterStateFromTemplate,
  createDefaultImageState,
  createEmptyHeaderFooterState,
  validationFieldHasPrefix,
  type HeaderFooterSlotKey,
  type HeaderFooterSlotState,
  type HeaderFooterState
} from '~/components/library/lib/header-footer';
import { initialTemplateStyleFromFragment } from '~/components/library/lib/html-template-form';

/** Cheap content-equality for ValidationError arrays — used to short-circuit
 *  prop→state syncs and avoid update loops when callers reconstruct the
 *  array on every render. */
function validationErrorsEqual(a: ValidationError[], b: ValidationError[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ea = a[i];
    const eb = b[i];
    if (ea.message !== eb.message) return false;
    if (ea.field.length !== eb.field.length) return false;
    for (let j = 0; j < ea.field.length; j++) {
      if (ea.field[j] !== eb.field[j]) return false;
    }
  }
  return true;
}

/**
 * Form-state plumbing shared by the create- and edit-style flows: the
 * `HtmlDocumentTemplateInput` payload, the 6-slot header/footer state, the
 * per-template style overrides, validation errors, and the visibility selector
 * value. Components only render; this hook owns the mutations.
 *
 * Word templates use a smaller dedicated state hook because they don't carry
 * header/footer or style data.
 */
export function useHtmlEditorFormState(params: {
  initialForm: HtmlDocumentTemplateInput;
  initialTemplate?: HtmlDocumentTemplateFieldsFragment;
  initialValidationErrors?: ValidationError[];
}) {
  const [form, setForm] = React.useState<HtmlDocumentTemplateInput>(params.initialForm);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    params.initialValidationErrors ?? []
  );
  const [headerFooter, setHeaderFooter] = React.useState<HeaderFooterState>(() =>
    buildHeaderFooterStateFromTemplate(params.initialTemplate)
  );
  const [templateStyleForm, setTemplateStyleForm] =
    React.useState<HtmlDocumentTemplateStyleInput | null>(() =>
      initialTemplateStyleFromFragment(params.initialTemplate)
    );

  const defaultVisibility = useDefaultVisibilityValue();
  const initialVisibilityFragment = params.initialTemplate?.visibility ?? null;
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(
    initialVisibilityFragment
      ? visibilityValueFromFragment(initialVisibilityFragment)
      : defaultVisibility
  );

  React.useEffect(() => {
    setVisibility(
      initialVisibilityFragment
        ? visibilityValueFromFragment(initialVisibilityFragment)
        : defaultVisibility
    );
  }, [defaultVisibility, initialVisibilityFragment, params.initialTemplate?.id]);

  React.useEffect(() => {
    if (params.initialTemplate) {
      setHeaderFooter(buildHeaderFooterStateFromTemplate(params.initialTemplate));
    } else {
      setHeaderFooter(createEmptyHeaderFooterState());
    }
  }, [params.initialTemplate?.id, params.initialTemplate?.headerFooter]);

  // Mirror externally-supplied validation errors into local state when the
  // caller hands us a genuinely new error set. We track the *previous prop*
  // (not local state) so user-driven local mutations like
  // `clearValidationErrorForField` aren't stomped on the next re-render.
  // Comparing prop-to-prop also prevents an update loop when the caller
  // reconstructs the same array on every render: same content => no setState,
  // no re-render. The empty-array guard preserves the existing UX (a
  // successful submit with no errors leaves prior errors visible until the
  // next attempted submit produces new ones).
  const lastInitialErrorsRef = React.useRef<ValidationError[] | undefined>(
    params.initialValidationErrors
  );
  React.useEffect(() => {
    const prev = lastInitialErrorsRef.current;
    const next = params.initialValidationErrors;
    if (prev === next) return;
    lastInitialErrorsRef.current = next;
    if (!next?.length) return;
    if (validationErrorsEqual(prev ?? [], next)) return;
    setValidationErrors(next);
  }, [params.initialValidationErrors]);

  const { updateForm: updateFormBase, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  const clearValidationErrorForField = React.useCallback(
    (fieldParts: string[]) => {
      setValidationErrors((prev) => prev.filter((e) => !getValidationError([e], fieldParts)));
    },
    [getValidationError]
  );

  const clearValidationErrorsWithPrefix = React.useCallback((prefixes: string[][]) => {
    setValidationErrors((prev) =>
      prev.filter((e) => !prefixes.some((p) => validationFieldHasPrefix(e.field, p)))
    );
  }, []);

  const updateHeaderFooterSlot = React.useCallback(
    (
      slotKey: HeaderFooterSlotKey,
      updater: (slot: HeaderFooterSlotState) => HeaderFooterSlotState
    ) => {
      setHeaderFooter((prev) => ({ ...prev, [slotKey]: updater(prev[slotKey]) }));
    },
    []
  );

  const setHeaderFooterSlotMode = React.useCallback(
    (slotKey: HeaderFooterSlotKey, mode: 'text' | 'image') => {
      updateHeaderFooterSlot(slotKey, (slot) => {
        if (slot.mode === mode) return slot;
        if (mode === 'text') {
          return { mode: 'text', text: slot.text, image: createDefaultImageState() };
        }
        const nextImage =
          slot.image.fileId || slot.image.upload || slot.image.existing
            ? slot.image
            : createDefaultImageState();
        return { mode: 'image', text: slot.text, image: nextImage };
      });
    },
    [updateHeaderFooterSlot]
  );

  return {
    form,
    setForm,
    updateForm: updateFormBase,
    getValidationError,
    validationErrors,
    setValidationErrors,
    clearValidationErrorForField,
    clearValidationErrorsWithPrefix,
    headerFooter,
    setHeaderFooter,
    updateHeaderFooterSlot,
    setHeaderFooterSlotMode,
    templateStyleForm,
    setTemplateStyleForm,
    visibility,
    setVisibility
  };
}

export type UseHtmlEditorFormStateResult = ReturnType<typeof useHtmlEditorFormState>;
